import { format } from "date-fns";
import { ArrowRight, CalendarIcon, X, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchBar } from "@/components/common/search-bar";
import { UserRequestSort } from "../types/user-request";
import {
  StatusFilterValue,
  useUserRequestFilters,
} from "../hooks/use-user-request-filters";

const SORT_OPTIONS: Array<{ value: UserRequestSort; label: string }> = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

interface UserRequestFilterBarProps {
  statusOptions: Array<{ value: StatusFilterValue; label: string }>;
}

// Shared filter row for both Pending Requests tabs. Reads/writes the URL via
// useUserRequestFilters; search is the standard <SearchBar>. statusOptions
// differ per request type, so they're passed in.
export default function UserRequestFilterBar({
  statusOptions,
}: UserRequestFilterBarProps) {
  const {
    status,
    sort,
    dateFrom,
    dateTo,
    filtersActive,
    setStatus,
    setSort,
    setDateRange,
    reset,
  } = useUserRequestFilters();

  const from = dateFrom ? new Date(dateFrom) : undefined;
  const to = dateTo ? new Date(dateTo) : undefined;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`w-[240px] justify-start text-left font-normal bg-white text-gray-900 border-gray-300 hover:bg-gray-50 overflow-hidden ${
              !from && "text-gray-500"
            }`}
          >
            <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {from && to ? (
                <>
                  {format(from, "MMM dd, yy")} - {format(to, "MMM dd, yy")}
                </>
              ) : (
                "Pick a Date Range"
              )}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="start">
          <div className="flex items-end gap-3 mb-4 px-4">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1.5 block">
                Start
              </label>
              <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                {from ? (
                  format(from, "LLL dd, y")
                ) : (
                  <span className="text-muted-foreground">Select</span>
                )}
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1.5 block">
                End
              </label>
              <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                {to ? (
                  format(to, "LLL dd, y")
                ) : (
                  <span className="text-muted-foreground">Select</span>
                )}
              </div>
            </div>
            {(from || to) && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 mb-0 text-muted-foreground hover:text-foreground"
                onClick={() => setDateRange(null, null)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Calendar
            initialFocus
            mode="range"
            selected={{ from, to }}
            onSelect={(range) =>
              setDateRange(range?.from || null, range?.to || null)
            }
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>

      <Select
        value={status}
        onValueChange={(v) => setStatus(v as StatusFilterValue)}
      >
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          {statusOptions.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={sort} onValueChange={(v) => setSort(v as UserRequestSort)}>
        <SelectTrigger className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="w-[260px]">
        <SearchBar
          placeholder="Search name, email, phone…"
          urlParamStrategy="prefix"
          clearButton={{
            enabled: true,
            position: "inside",
            ariaLabel: "Clear search",
            icon: <XIcon className="w-4 h-4" />,
          }}
        />
      </div>

      {filtersActive && (
        <Button variant="outline" size="sm" onClick={reset} className="ml-auto">
          <X className="h-4 w-4 mr-1" />
          Reset Filters
        </Button>
      )}
    </div>
  );
}
